import React, { useEffect, useState } from "react";
import { Warehouse as WarehouseIcon, ClipboardCheck, FileSignature, Camera, Images } from "lucide-react";
import { ItemGalleryDialog, galleryFromItem, galleryUrl } from "../../components/ItemGallery";
import { toast } from "sonner";
import { API, fmtErr, money, fmtDate } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { useAuth, isManagerUp } from "../../lib/auth";
import { shopLogoUrl } from "../../lib/branding";
import { printDoc } from "../../lib/printDoc";
import { StatusBadge } from "../../components/StatusBadge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const CONDITIONS = ["Good", "Fair", "Poor", "Damaged"];
const RELATIONS = ["Client (owner)", "Family", "Representative", "Other"];

export default function Warehouse() {
  const { t } = useLang();
  const { user } = useAuth();
  const managerUp = isManagerUp(user);
  const [items, setItems] = useState(null);
  const [inspecting, setInspecting] = useState(null);
  const [inspectForm, setInspectForm] = useState({ condition: "Good", storage_location: "", fuel_percent: "", mileage_km: "", notes: "" });
  const [inspectPhoto, setInspectPhoto] = useState(null);
  const [releaseTarget, setReleaseTarget] = useState(null);
  const [releaseForm, setReleaseForm] = useState({ collected_by: "", id_number: "", relation: "Client (owner)", condition: "Good", notes: "" });
  const [releasePhoto, setReleasePhoto] = useState(null);
  const [release, setRelease] = useState(null);
  const [busy, setBusy] = useState(false);
  const [printMode, setPrintMode] = useState("a4");
  const [galleryItem, setGalleryItem] = useState(null);
  const [draftMark, setDraftMark] = useState(false);

  const load = () => API.get("/shop/inventory").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  if (!items) return <p className="text-slate-500">{t("common.loading")}</p>;

  const awaiting = items.filter((i) => i.status === "pledged" && i.warehouse_status !== "in_warehouse");
  const inWarehouse = items.filter((i) => i.status === "pledged" && i.warehouse_status === "in_warehouse");
  const redeemed = items.filter((i) => i.status === "redeemed");

  const submitInspect = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(inspectForm).forEach(([k, v]) => v && fd.append(k, v));
      if (inspectPhoto) fd.append("photo", inspectPhoto);
      await API.post(`/shop/items/${inspecting.id}/inspect`, fd);
      toast.success(t("msg.saved"));
      setInspecting(null);
      setInspectPhoto(null);
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const openRelease = (item) => {
    setReleaseTarget(item);
    setReleaseForm({ collected_by: item.customer_name || "", id_number: "", relation: "Client (owner)", condition: "Good", notes: "" });
    setReleasePhoto(null);
  };

  const confirmRelease = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(releaseForm).forEach(([k, v]) => v && fd.append(k, v));
      if (releasePhoto) fd.append("photo", releasePhoto);
      const { data } = await API.post(`/shop/loans/${releaseTarget.loan_id}/release-note`, fd);
      setRelease(data);
      setReleaseTarget(null);
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const ItemTable = ({ list, action, testPrefix }) => (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("common.name")}</TableHead>
            <TableHead>{t("items.category")}</TableHead>
            <TableHead>{t("items.loan")}</TableHead>
            <TableHead>{t("loans.customer")}</TableHead>
            <TableHead>{t("items.location")}</TableHead>
            <TableHead>{t("wh.inspection")}</TableHead>
            <TableHead className="text-right">{t("common.actions")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {list.length === 0 && (
            <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">{t("wh.empty")}</TableCell></TableRow>
          )}
          {list.map((i) => (
            <TableRow key={i.id} data-testid={`${testPrefix}-${i.id.slice(0, 8)}`}>
              <TableCell className="font-medium">{i.name}</TableCell>
              <TableCell>{i.category}</TableCell>
              <TableCell className="font-mono text-sm">{i.loan_number || "—"}</TableCell>
              <TableCell>{i.customer_name || "—"}</TableCell>
              <TableCell>{i.storage_location || "—"}</TableCell>
              <TableCell className="text-xs text-slate-500">
                {i.inspected_at ? `${i.inspection_condition || "OK"} · ${i.inspected_by} · ${fmtDate(i.inspected_at)}` : "—"}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-2">
                  <button data-testid={`gallery-open-${i.id.slice(0, 8)}`} onClick={() => setGalleryItem(i)} title={t("gal.title")}
                    className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg border border-slate-300 text-xs hover:bg-slate-50">
                    <Images className="h-3.5 w-3.5" />{galleryFromItem(i).length}
                  </button>
                  {action(i)}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <div className="space-y-8" data-testid="warehouse-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900 flex items-center gap-3">
        <WarehouseIcon className="h-7 w-7 text-emerald-600" />{t("wh.title")}
      </h1>

      <section>
        <h2 className="font-heading text-lg font-semibold text-amber-700 mb-4">{t("wh.awaiting")} ({awaiting.length})</h2>
        <ItemTable list={awaiting} testPrefix="wh-awaiting" action={(i) => (!managerUp ? null :
          <Button size="sm" data-testid={`inspect-item-${i.id.slice(0, 8)}`}
            onClick={() => { setInspecting(i); setInspectForm({ condition: "Good", storage_location: i.storage_location || "", fuel_percent: i.fuel_percent || "", mileage_km: i.mileage_km || "", notes: "" }); setInspectPhoto(null); }}
            className="bg-emerald-600 hover:bg-emerald-700 text-white">
            <ClipboardCheck className="h-4 w-4 mr-1" />{t("wh.receive")}
          </Button>
        )} />
      </section>

      <section>
        <h2 className="font-heading text-lg font-semibold text-emerald-700 mb-4">{t("wh.in_warehouse")} ({inWarehouse.length})</h2>
        <ItemTable list={inWarehouse} testPrefix="wh-stored" action={() => <StatusBadge status="pledged" />} />
      </section>

      <section>
        <h2 className="font-heading text-lg font-semibold text-blue-700 mb-4">{t("wh.ready_release")} ({redeemed.length})</h2>
        <ItemTable list={redeemed} testPrefix="wh-redeemed" action={(i) => (!managerUp ? null :
          i.warehouse_status === "released" ? (
            <span className="text-xs font-medium text-blue-700" data-testid={`released-label-${i.id.slice(0, 8)}`}>{t("wh.released")} · {fmtDate(i.released_at)}</span>
          ) : (
            <Button size="sm" data-testid={`release-item-${i.id.slice(0, 8)}`} onClick={() => openRelease(i)}
              className="bg-blue-600 hover:bg-blue-700 text-white">
              <FileSignature className="h-4 w-4 mr-1" />{t("wh.release_note")}
            </Button>
          )
        )} />
      </section>

      <ItemGalleryDialog item={galleryItem} onClose={() => setGalleryItem(null)} managerUp={managerUp} onChanged={load} />

      <Dialog open={!!inspecting} onOpenChange={(o) => !o && setInspecting(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="inspect-dialog">
          <DialogHeader><DialogTitle className="font-heading">{t("wh.confirm_receipt")}</DialogTitle></DialogHeader>
          {inspecting && (
            <div className="border border-slate-200 rounded-lg p-3 text-sm mb-1">
              <div className="font-mono text-xs text-slate-500">{inspecting.loan_number ? `CTR-${new Date().getFullYear()}-${inspecting.loan_number.replace("LN-", "")}` : "—"}</div>
              <div className="font-semibold">{inspecting.name}</div>
              <div className="text-xs text-slate-500">{t("rcp.client")}: {inspecting.customer_name || "—"}</div>
            </div>
          )}
          <form onSubmit={submitInspect} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">{t("items.condition")}</Label>
                <Select value={inspectForm.condition} onValueChange={(v) => setInspectForm({ ...inspectForm, condition: v })}>
                  <SelectTrigger data-testid="inspect-condition-select" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {CONDITIONS.map((c) => <SelectItem key={c} value={c} data-testid={`inspect-cond-${c.toLowerCase()}`}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">{t("wh.fuel")}</Label>
                <Input data-testid="inspect-fuel-input" type="number" min="0" max="100" value={inspectForm.fuel_percent}
                  onChange={(e) => setInspectForm({ ...inspectForm, fuel_percent: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">{t("wh.mileage")}</Label>
                <Input data-testid="inspect-mileage-input" type="number" min="0" value={inspectForm.mileage_km}
                  onChange={(e) => setInspectForm({ ...inspectForm, mileage_km: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">{t("wh.photo_receipt")}</Label>
                <label className="mt-1 flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-sm cursor-pointer hover:bg-slate-50">
                  <Camera className="h-4 w-4 text-slate-500" />
                  <span className="truncate text-slate-600">{inspectPhoto ? inspectPhoto.name : "Choose File"}</span>
                  <input type="file" accept="image/*" className="hidden" data-testid="inspect-photo-input"
                    onChange={(e) => setInspectPhoto(e.target.files[0])} />
                </label>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">{t("items.location")}</Label>
              <Input data-testid="inspect-location-input" value={inspectForm.storage_location}
                onChange={(e) => setInspectForm({ ...inspectForm, storage_location: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">{t("wh.damage_log")}</Label>
              <textarea data-testid="inspect-notes-input" rows={2} value={inspectForm.notes}
                onChange={(e) => setInspectForm({ ...inspectForm, notes: e.target.value })}
                placeholder="e.g. small scratch on rear bumper, missing spare tyre"
                className="mt-1 w-full rounded-md border border-input bg-white px-3 py-2 text-sm" />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setInspecting(null)}>{t("common.cancel")}</Button>
              <Button data-testid="inspect-save-button" type="submit" disabled={busy} className="bg-[var(--brand,#1E3A5F)] hover:bg-[#16294d] text-white">
                <ClipboardCheck className="h-4 w-4 mr-1.5" />{t("wh.ack")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!releaseTarget} onOpenChange={(o) => !o && setReleaseTarget(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="release-form-dialog">
          <DialogHeader><DialogTitle className="font-heading">{t("rel.to_client")}</DialogTitle></DialogHeader>
          {releaseTarget && (
            <div className="border border-slate-200 rounded-lg p-3 text-sm mb-1">
              <div className="font-mono text-xs text-slate-500">{releaseTarget.loan_number ? `CTR-${new Date().getFullYear()}-${releaseTarget.loan_number.replace("LN-", "")}` : "—"}</div>
              <div className="font-semibold">{releaseTarget.name}</div>
              <div className="text-xs text-slate-500">{t("rcp.client")}: {releaseTarget.customer_name || "—"}</div>
            </div>
          )}
          <form onSubmit={confirmRelease} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">{t("rel.collected_by")} *</Label>
                <Input data-testid="release-collected-input" required value={releaseForm.collected_by}
                  onChange={(e) => setReleaseForm({ ...releaseForm, collected_by: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">{t("rel.id_number")}</Label>
                <Input data-testid="release-id-input" value={releaseForm.id_number}
                  onChange={(e) => setReleaseForm({ ...releaseForm, id_number: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">{t("rel.relation")}</Label>
                <Select value={releaseForm.relation} onValueChange={(v) => setReleaseForm({ ...releaseForm, relation: v })}>
                  <SelectTrigger data-testid="release-relation-select" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {RELATIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">{t("rel.condition_release")}</Label>
                <Select value={releaseForm.condition} onValueChange={(v) => setReleaseForm({ ...releaseForm, condition: v })}>
                  <SelectTrigger data-testid="release-condition-select" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">{t("rel.photo")}</Label>
              <label className="mt-1 flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-sm cursor-pointer hover:bg-slate-50">
                <Camera className="h-4 w-4 text-slate-500" />
                <span className="truncate text-slate-600">{releasePhoto ? releasePhoto.name : "Choose File"}</span>
                <input type="file" accept="image/*" className="hidden" data-testid="release-photo-input"
                  onChange={(e) => setReleasePhoto(e.target.files[0])} />
              </label>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">{t("common.notes")}</Label>
              <textarea data-testid="release-notes-input" rows={2} value={releaseForm.notes}
                onChange={(e) => setReleaseForm({ ...releaseForm, notes: e.target.value })}
                placeholder="e.g. keys + documents handed over"
                className="mt-1 w-full rounded-md border border-input bg-white px-3 py-2 text-sm" />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setReleaseTarget(null)}>{t("common.cancel")}</Button>
              <Button data-testid="release-confirm-button" type="submit" disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white">
                <FileSignature className="h-4 w-4 mr-1.5" />{t("rel.confirm")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!release} onOpenChange={(o) => !o && setRelease(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="release-dialog">
          {release && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{t("rel.title")}</DialogTitle></DialogHeader>
              <div className={`print-area bg-white text-sm ${printMode === "thermal" ? "thermal-doc" : ""}`} data-testid="release-body">
                {draftMark && <div className="draft-watermark" data-testid="draft-watermark-release">DRAFT</div>}
                <div className="flex items-center gap-4 border-b-2 border-[var(--brand,#1E3A5F)] pb-3">
                  {release.shop.logo_path ? (
                    <img src={shopLogoUrl(release.shop)} alt={release.shop.name} data-testid="release-logo"
                      className="h-14 w-14 shrink-0 rounded-full border-2 border-[var(--brand,#1E3A5F)] object-cover" />
                  ) : (
                    <div className="h-14 w-14 shrink-0 rounded-full border-2 border-[var(--brand,#1E3A5F)] flex items-center justify-center font-heading font-bold text-[var(--brand,#1E3A5F)] text-lg">
                      {release.shop.name.split(/\s+/).map((w) => w[0]).slice(0, 2).join("")}
                    </div>
                  )}
                  <div>
                    <div className="font-heading font-bold text-xl text-[var(--brand,#1E3A5F)] uppercase tracking-tight">{release.shop.name}</div>
                    {release.shop.header_text && <div className="text-xs text-slate-600">{release.shop.header_text}</div>}
                    {release.shop.company_address && <div className="text-xs text-slate-500">{release.shop.company_address}</div>}
                  </div>
                </div>
                <h2 className="text-center font-heading font-bold text-xl tracking-wide my-4">{t("rel.title")}</h2>
                <table className="w-full text-xs border border-slate-300 mb-4">
                  <tbody>
                    <tr className="border-b border-slate-200">
                      <td className="p-2 font-bold text-[var(--brand,#1E3A5F)] bg-slate-50 w-[20%]">No.</td>
                      <td className="p-2 font-mono" data-testid="release-no">{release.release_no}</td>
                      <td className="p-2 font-bold text-[var(--brand,#1E3A5F)] bg-slate-50 w-[20%]">{t("loans.number")}</td>
                      <td className="p-2 font-mono">{release.loan.loan_number}</td>
                    </tr>
                    <tr className="border-b border-slate-200">
                      <td className="p-2 font-bold text-[var(--brand,#1E3A5F)] bg-slate-50">{t("rcp.client")}</td>
                      <td className="p-2">{release.customer?.name}</td>
                      <td className="p-2 font-bold text-[var(--brand,#1E3A5F)] bg-slate-50">{t("common.date")}</td>
                      <td className="p-2 font-mono">{fmtDate(release.released_at)}</td>
                    </tr>
                    {release.release && (
                      <tr className="border-b border-slate-200">
                        <td className="p-2 font-bold text-[var(--brand,#1E3A5F)] bg-slate-50">{t("rel.collected_by")}</td>
                        <td className="p-2">{release.release.collected_by}{release.release.id_number ? ` · ID ${release.release.id_number}` : ""}{release.release.relation ? ` (${release.release.relation})` : ""}</td>
                        <td className="p-2 font-bold text-[var(--brand,#1E3A5F)] bg-slate-50">{t("rel.condition_release")}</td>
                        <td className="p-2">{release.release.condition || "—"}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <p className="text-xs leading-relaxed text-slate-700 mb-4">{t("rel.text")}</p>
                <table className="w-full text-xs border border-slate-300">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="text-left p-2 border-b border-slate-300">{t("common.name")}</th>
                      <th className="text-left p-2 border-b border-slate-300">{t("items.category")}</th>
                      <th className="text-left p-2 border-b border-slate-300">{t("items.serial")}</th>
                      <th className="text-left p-2 border-b border-slate-300">{t("items.condition")}</th>
                      <th className="text-right p-2 border-b border-slate-300">{t("items.est_value")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {release.items.map((i) => (
                      <tr key={i.id}>
                        <td className="p-2 border-b border-slate-200">{i.name}</td>
                        <td className="p-2 border-b border-slate-200">{i.category}</td>
                        <td className="p-2 border-b border-slate-200 font-mono">{i.serial_no || i.plate_no || "—"}</td>
                        <td className="p-2 border-b border-slate-200">{i.inspection_condition || i.condition || "—"}</td>
                        <td className="p-2 border-b border-slate-200 text-right font-mono">{money(i.estimated_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {release.items.some((i) => i.photo_path || i.receipt_photo_path) || release.release?.photo_path ? (
                  <div className="mb-4" data-testid="release-condition-compare">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--brand,#1E3A5F)] mb-1">{t("rel.condition_compare")}</div>
                    <table className="w-full text-xs border border-slate-300">
                      <thead><tr className="bg-slate-50 text-[var(--brand,#1E3A5F)]">
                        <th className="p-1.5 text-left">{t("common.name")}</th><th className="p-1.5">{t("rel.photo_intake")}</th><th className="p-1.5">{t("rel.photo_inspection")}</th><th className="p-1.5">{t("rel.photo_release")}</th>
                      </tr></thead>
                      <tbody>
                        {release.items.map((i) => (
                          <tr key={i.id} className="border-t border-slate-200">
                            <td className="p-1.5 align-top"><div className="font-medium">{i.name}</div><div className="text-slate-500">{i.condition || "—"} → {i.inspection_condition || "—"} → {release.release?.condition || "—"}</div></td>
                            {[["", i.photo_path], ["receipt", i.receipt_photo_path], ["release", release.release?.photo_path]].map(([which, has], k) => (
                              <td key={k} className="p-1.5 text-center">
                                {has ? <img src={galleryUrl(i.id, which)} alt="" className="h-24 w-24 object-cover rounded border border-slate-200 inline-block" data-testid={`compare-${which || "intake"}-${i.id.slice(0, 8)}`} /> : <span className="text-slate-300">—</span>}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
                <div className="grid grid-cols-2 gap-8 mt-14 text-center text-xs">
                  <div>
                    <div className="border-t border-slate-500 pt-2 font-semibold">{t("rel.authorized")}</div>
                    <div className="text-slate-600 mt-1">{release.authorized_by}</div>
                  </div>
                  <div>
                    <div className="border-t border-slate-500 pt-2 font-semibold">{t("rel.received_by")}</div>
                    <div className="text-slate-600 mt-1">{release.customer?.name}</div>
                  </div>
                </div>
                <div className="border-t border-[var(--brand,#1E3A5F)] mt-8 pt-2 text-[10px] text-slate-500 text-center">
                  © {new Date().getFullYear()} {release.shop.name}. All Rights Reserved.
                  {release.shop.footer_text && <div className="mt-1">{release.shop.footer_text}</div>}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-4">
                <div className="flex rounded-lg border border-slate-300 overflow-hidden text-xs">
                  <button data-testid="release-print-mode-a4" onClick={() => setPrintMode("a4")}
                    className={`px-3 py-1.5 ${printMode === "a4" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>{t("print.mode_a4")}</button>
                  <button data-testid="release-print-mode-thermal" onClick={() => setPrintMode("thermal")}
                    className={`px-3 py-1.5 ${printMode === "thermal" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>{t("print.mode_thermal")}</button>
                </div>
                <button data-testid="release-print-draft-toggle" onClick={() => setDraftMark(!draftMark)}
                  className={`px-3 py-1.5 rounded-lg border text-xs ${draftMark ? "border-red-400 bg-red-50 text-red-700" : "border-slate-300 text-slate-600"}`}>
                  {t("print.draft")}
                </button>
                <Button data-testid="release-print-button"
                  onClick={() => printDoc(document.querySelector('[data-testid="release-body"]'), { mode: printMode, draft: draftMark, title: release.release_no })}
                  className="flex-1 bg-slate-900 hover:bg-slate-800 text-white">
                  {t("repay.print")}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
